/**
 * Channel News Modal - News feed for a group
 */

import React, { useState } from 'react';
import { Modal, List, Button, Input, Form, Empty, Popconfirm, Typography, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from 'react-query';
import api from '../../config/api';

const { TextArea } = Input;
const { Text } = Typography;

interface NewsItem {
  id: string;
  title: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  user: { id: string; firstName: string; lastName: string };
  history?: Array<{ title: string; content: string; editedAt: string; editedBy: { id: string; firstName: string; lastName: string } }>;
}

interface ChannelNewsModalProps {
  channelId: string;
  open: boolean;
  onClose: () => void;
}

export const ChannelNewsModal: React.FC<ChannelNewsModalProps> = ({ channelId, open, onClose }) => {
  const queryClient = useQueryClient();
  const [createVisible, setCreateVisible] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const { data, isLoading } = useQuery(
    ['channel-news', channelId],
    () => api.get(`/api/chat/channels/${channelId}/news`).then(res => res.data),
    { enabled: !!channelId && open }
  );

  const news: NewsItem[] = data?.news || [];

  const handleCreate = async (values: { title: string; content: string }) => {
    try {
      await api.post(`/api/chat/channels/${channelId}/news`, {
        title: values.title.trim(),
        content: values.content.trim(),
      });
      message.success('News added');
      queryClient.invalidateQueries(['channel-news', channelId]);
      setCreateVisible(false);
      createForm.resetFields();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to add news');
    }
  };

  const handleUpdate = async (newsId: string, values: { title: string; content: string }) => {
    try {
      await api.put(`/api/chat/channels/${channelId}/news/${newsId}`, {
        title: values.title.trim(),
        content: values.content.trim(),
      });
      message.success('News updated');
      queryClient.invalidateQueries(['channel-news', channelId]);
      setEditingId(null);
      editForm.resetFields();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to update news');
    }
  };

  const handleDelete = async (newsId: string) => {
    try {
      await api.delete(`/api/chat/channels/${channelId}/news/${newsId}`);
      message.success('News deleted');
      queryClient.invalidateQueries(['channel-news', channelId]);
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to delete news');
    }
  };

  const startEdit = (item: NewsItem) => {
    setEditingId(item.id);
    editForm.setFieldsValue({ title: item.title, content: item.content });
  };

  const cancelEdit = () => {
    setEditingId(null);
    editForm.resetFields();
  };

  return (
    <Modal
      title="News Feed"
      open={open}
      onCancel={onClose}
      footer={null}
      width={560}
      destroyOnClose
    >
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateVisible(true)}>
          Add News
        </Button>
      </div>

      {createVisible && (
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreate}
          style={{ marginBottom: 24, padding: 16, background: '#fafafa', borderRadius: 8 }}
        >
          <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Title is required' }]}>
            <Input placeholder="News title" maxLength={255} />
          </Form.Item>
          <Form.Item name="content" label="Content" rules={[{ required: true, message: 'Content is required' }]}>
            <TextArea rows={3} placeholder="News content" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit">
              Post
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={() => { setCreateVisible(false); createForm.resetFields(); }}>
              Cancel
            </Button>
          </Form.Item>
        </Form>
      )}

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div>
      ) : news.length === 0 ? (
        <Empty description="No news yet" style={{ marginTop: 40 }} />
      ) : (
        <List
          dataSource={news}
          renderItem={(item) => (
            <List.Item
              actions={
                editingId === item.id
                  ? [
                      <Button key="save" type="link" size="small" onClick={() => editForm.submit()}>
                        Save
                      </Button>,
                      <Button key="cancel" type="link" size="small" onClick={cancelEdit}>
                        Cancel
                      </Button>,
                    ]
                  : [
                      <Button key="edit" type="link" size="small" icon={<EditOutlined />} onClick={() => startEdit(item)}>
                        Edit
                      </Button>,
                      <Popconfirm
                        key="delete"
                        title="Delete this news?"
                        onConfirm={() => handleDelete(item.id)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                          Delete
                        </Button>
                      </Popconfirm>,
                    ]
              }
            >
              {editingId === item.id ? (
                <Form
                  form={editForm}
                  layout="vertical"
                  style={{ width: '100%' }}
                  onFinish={(v) => handleUpdate(item.id, v)}
                >
                  <Form.Item name="title" rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                  <Form.Item name="content" rules={[{ required: true }]}>
                    <TextArea rows={3} />
                  </Form.Item>
                </Form>
              ) : (
                <div style={{ width: '100%' }}>
                  <Text strong>{item.title}</Text>
                  <div style={{ marginTop: 4, color: '#666', fontSize: 13 }}>{item.content}</div>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                    {item.user?.firstName} {item.user?.lastName} · {item.createdAt ? new Date(item.createdAt).toLocaleString() : ''}
                  </Text>
                </div>
              )}
            </List.Item>
          )}
        />
      )}
    </Modal>
  );
};
